/* eslint-disable max-lines */
import React, { useCallback, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import _ from 'lodash';
import moment from 'moment';
import QueryString from 'query-string';
import GridChipFilterContainer from 'components/GridChipFilterContainer/GridChipFilterContainer';
import {
  convertSearchToRequest,
  trackSearch,
  AUTOCOMPLETE_TYPES
} from 'components/GridChipFilterContainer/helpers';
import { Flex, WhiteSection } from 'components/base/layout-components';
import TeamsEmptyState from 'components/teams/TeamsEmptyState';
import { matchTeamMetricsToTeam } from 'common/services/teams.service';
import * as TrackingService from 'common/services/tracking.service';
import { metricNames } from '@linearb/linjs-common';
import {
  convertMetricsNameToV3,
  getMetricsV3
} from 'common/services/metrics.service';
import { getColumns, getTeamsTableData } from './teams.service';
import debounce from 'debounce-promise';
import {
  TRACKING,
  SEARCH_DATE_FORMAT,
  SETTING_NAMES
} from 'common/constants/constants';
import { getMinDateForDatePicker } from 'common/utils/utils';
import useSettingsService from 'components/hooks/useSettingsService';
import useMetricsCalculationConfig from 'views/root/views/home/hooks/useMetricsCalculationConfig';
import useTeams from '../../../settings/views/ContributorsAndTeams/Teams/useTeams';
import { teamsSelector, getRootTeamSelector } from 'state/selectors/teams';
import { useLocation, useHistory } from 'react-router';
import { getAccounts } from 'state/callers';
import { accountsSelector } from 'state/selectors/account';
import { SELECTED_TIME_RANGE_CODE } from 'common/constants/people';
import useBenchmarkOrgSizeConfig from 'views/root/views/home/hooks/useBenchmarkOrgSizeConfig';

const filterChipLabelMap = {
  teams: 'Team'
};

const ALL_METRICS = [
  metricNames.ACTIVE_BRANCHES,
  metricNames.DONE_BRANCHES,
  metricNames.CYCLE_TIME,
  metricNames.REVIEWS,
  metricNames.REVIEW_DEPTH,
  metricNames.ACTIVE_DAYS,
  metricNames.COMMIT_CODE_CHANGES,
  metricNames.NEW_WORK,
  metricNames.REWORK,
  metricNames.REFACTOR,
  metricNames.PR_NEW,
  metricNames.TIME_TO_PR,
  metricNames.TIME_TO_REVIEW,
  metricNames.REVIEW_TIME,
  metricNames.TIME_TO_PROD,
  metricNames.COMMIT_INVOLVED_REPOS_COUNT
];

// eslint-disable-next-line max-lines-per-function
const Teams = ({
  selectedTeam,
  currentTeams = [],
  contributors = [],
  iterationsPickerData,
  latestIteration,
  currentIteration,
  selectTeamId,
  getTeams,
  createTeam,
  updateTeam,
  deleteTeam,
  updateTeamContributors,
  toggleModal,
  toggleSnackBar,
  isTeamLicense,
  license,
  account,
  gitScopeToggle
}) => {
  const teams = useSelector(teamsSelector);
  const history = useHistory();
  const location = useLocation();
  const dispatch = useDispatch();
  const accounts = useSelector(accountsSelector) || [];

  const TABLE_SKELETONS = [
    'avatar-singleline',
    'singleline-random',
    'singleline-random',
    'singleline-random',
    'singleline-random',
    'singleline-random',
    'singleline-random',
    'singleline',
    'singleline-random',
    'singleline-random',
    'singleline-random',
    ''
  ];
  const metricsCalculationConfig = useMetricsCalculationConfig();
  const { metricsCalculationStrategy } = metricsCalculationConfig;
  const { benchmarkOrgSize } = useBenchmarkOrgSizeConfig();

  const initialMetricsState = {
    items: [],
    length: 0
  };
  const { getSettingOrDefinitionValue } = useSettingsService();
  const showPersonalInfo = getSettingOrDefinitionValue(
    {
      name: SETTING_NAMES.SHOW_PERSONAL_INFORMATION
    },
    true
  );
  const rootTeamId = useSelector(getRootTeamSelector);
  const filterBySelectedTeam = rootTeamId;

  const [metrics, setMetrics] = useState(initialMetricsState);
  const [returnNoData] = useState(true);

  const { onConfigureTeam, onConfigureGroup, onDeleteGroup, onDeleteTeam } =
    useTeams();

  const onDeleteTeamClick = async (selectedTeam, accounts) => {
    onDeleteTeam(selectedTeam, accounts);
  };

  const onEditTeamClick = selectedTeam => {
    TrackingService.logEvent(TRACKING.EVENTS.CLICK, {
      page: TRACKING.PAGES.PEOPLE,
      page_tab: TRACKING.PAGE_TABS.TEAMS,
      click_type: TRACKING.CLICK.EDIT_TEAM
    });
    onConfigureTeam({ team: selectedTeam });
  };

  const onEditGroupClick = selectedTeam => {
    onConfigureGroup(selectedTeam);
  };

  const onDeleteGroupClick = (selectedTeam, accounts) => {
    onDeleteGroup(selectedTeam, accounts);
  };

  const filtersConfig = {
    time: {
      type: 'date',
      reqKey: `time`,
      maxDate: () => moment().format(SEARCH_DATE_FORMAT),
      minDate: () => getMinDateForDatePicker(license)
    },
    teams: {
      type: 'multi-tree-dropdown',
      reqKey: 'team_ids',
      options: currentTeams,
      getAvatar: ({ initials: name, color }) => ({
        name,
        color,
        withContent: true
      })
    },
    order_by: {
      type: 'order_by',
      reqKey: 'order_by'
    }
  };

  const search = QueryString.parse(location.search, {
    ignoreQueryPrefix: true
  });

  const [startDate = null, endDate = null] = _.get(search, 'time', '').split(
    ','
  );

  /*
   * Check the time range
   * 0 If it doesn't match any iteration
   * 1 If it matches the current iteration
   * 2 If it is a retrospect iteration
   * @Note: this logic already exists as a reselector
   */
  const timeRangeIteration = iterationsPickerData
    .filter(iteration => !iteration.isComputed)
    .reduce((result, iteration) => {
      if (
        startDate === latestIteration.start_date &&
        endDate === latestIteration.end_date
      )
        return SELECTED_TIME_RANGE_CODE.CUSTOM;
      if (iteration.start_date === startDate && iteration.end_date === endDate)
        return SELECTED_TIME_RANGE_CODE.CURRENT_ITERATION;
      return result;
    }, SELECTED_TIME_RANGE_CODE.CUSTOM);

  const memoizedGetTeams = useCallback(
    debounce(
      async ({ order_by, filter, offset, limit, team_git_scope_disabled }) => {
        try {
          const timeFilter = filter.time || {
            before: moment().format(SEARCH_DATE_FORMAT),
            after: moment().subtract(12, 'months').format(SEARCH_DATE_FORMAT)
          };
          const metricsV3Data = await getMetricsV3({
            requested_metrics: convertMetricsNameToV3(
              ALL_METRICS,
              metricsCalculationStrategy
            ),
            group_by: 'team',
            limit,
            offset,
            roll_up: 'custom',
            team_ids: filter.team_ids || [],
            time_ranges: [timeFilter],
            return_no_data: true,
            order_by,
            team_git_scope_disabled
          });

          const mappedMetrics = metricsV3Data
            .map(({ group, data }) =>
              data.map(teamData => ({ ...teamData, team_id: group }))
            )
            .flat();

          const metrics = {
            items: mappedMetrics,
            total: filter.team_ids?.length
              ? mappedMetrics.length
              : teams?.length
          };
          setMetrics(metrics);

          return metrics;
        } catch (e) {
          window.TrackJS && window.TrackJS.track(e);
          setMetrics(Object.assign({}, initialMetricsState));
          return Promise.reject(e);
        }
      },
      1000
    ),
    [returnNoData, currentTeams]
  );

  const getReqFilterBody = () => {
    const search = QueryString.parse(location.search, {
      ignoreQueryPrefix: true
    });

    return convertSearchToRequest(search, filtersConfig);
  };

  useEffect(() => {
    const search = QueryString.parse(location.search, {
      ignoreQueryPrefix: true
    });

    trackSearch(
      TRACKING.EVENTS.FILTER,
      search,
      [],
      TRACKING.PAGES.PEOPLE,
      TRACKING.PAGE_TABS.TEAMS
    );
  }, [location.search]);

  const filtersGroupMap = [
    { type: 'date', names: 'time', defaultIsOpen: true, title: '' },
    // { type: 'autocomplete', name: 'contributors', defaultIsOpen: true, addAvatars: true },
    !isTeamLicense
      ? {
          type: 'autocompleteByType',
          items: [
            {
              type: AUTOCOMPLETE_TYPES.TREE_TEAMS_DROPDOWN,
              name: 'teams',
              addAvatars: true,
              multiple: true,
              isDisableChildrenOfSelectedParent: false
            }
          ],
          title: 'Teams',
          defaultIsOpen: true
        }
      : null
  ].filter(Boolean);

  let tableColumns = getColumns({ account });

  /**
   * Change columns according to the time range
   */
  switch (timeRangeIteration) {
    case SELECTED_TIME_RANGE_CODE.RETROSPECT:
      tableColumns = tableColumns.map(col => {
        const { name } = col;

        if (name === 'WIP') {
          return {
            dataPoint: metricNames.ACTIVE_BRANCHES,
            name: 'CARRYOVER',
            isSortedCol: true
          };
        }

        return col;
      });
      break;

    default:
      break;
  }

  useEffect(() => {
    const search = QueryString.parse(window.location.search, {
      ignoreQueryPrefix: true
    });
    const defaultFilters = {};

    if (!search.time) {
      defaultFilters.time = `${currentIteration.start_date},${currentIteration.end_date}`;
    }

    if (!search.order_by) {
      defaultFilters.order_by = `${metricNames.ACTIVE_BRANCHES},desc`;
    }

    if (Object.keys(defaultFilters).length) {
      history.replace({
        pathname: location.pathname,
        search: `?${QueryString.stringify(
          Object.assign({}, search, defaultFilters)
        )}`
      });
    }
  }, []);

  useEffect(() => {
    dispatch(getAccounts());
  }, []);

  return !currentTeams.length ? (
    <Flex height='50rem' justify='center' items='center'>
      <WhiteSection
        l='1rem'
        r='1rem'
        t='1rem'
        b='1rem'
        height='100%'
        width='100%'
      >
        <TeamsEmptyState
          imageWidth='42rem'
          textSize={'x-large'}
          trackingPage='PEOPLE'
          toggleModal={toggleModal}
          contributors={contributors}
          createTeam={createTeam}
          toggleSnackBar={toggleSnackBar}
          getTeams={getTeams}
        />
      </WhiteSection>
    </Flex>
  ) : (
    <>
      <GridChipFilterContainer
        disableDateFilterLimit
        filterBySelectedTeam={filterBySelectedTeam}
        disablePointInHistory
        getData={memoizedGetTeams}
        gitScopeToggle={gitScopeToggle}
        pageTag={TRACKING.PAGES.PEOPLE}
        pageTabTag={TRACKING.PAGE_TABS.TEAMS}
        data={metrics}
        tableData={() =>
          getTeamsTableData({
            data: matchTeamMetricsToTeam(currentTeams, metrics.items),
            time: {
              from: startDate,
              to: moment
                .min(moment(endDate), moment())
                .format(SEARCH_DATE_FORMAT)
            },
            onEditTeamClick,
            onDeleteTeamClick,
            onEditGroupClick,
            onDeleteGroupClick,
            isTeamLicense,
            toggleSnackBar,
            disableTeamLink: !showPersonalInfo,
            metricsCalculationConfig,
            benchmarkOrgSize,
            accounts
          })
        }
        filterChipLabelMap={filterChipLabelMap}
        columns={tableColumns}
        skeletonColumns={TABLE_SKELETONS}
        getReqFilterBody={getReqFilterBody}
        filtersConfig={filtersConfig}
        filtersGroupMap={filtersGroupMap}
        refreshOnDependencies={[...currentTeams, returnNoData]}
        lineHighlightOnHover={true}
      />
    </>
  );
};

export default Teams;
